import { createClient, type SupabaseClient } from '@supabase/supabase-js'

// The tenant serves /config.json with its own anon key (Caddy generates the
// response from container env), so the SPA ships no baked-in keys and the same
// bundle works in any tenant/environment it's deployed into.
//
// `url` comes back empty, meaning "same origin as this page". In dev, Vite
// proxies /config.json and every backend path to the tenant, so this resolves
// to http://localhost:5173 and still reaches the real backend.
type TenantConfig = { url: string; anonKey: string }

let client: SupabaseClient | null = null

// PostgREST answers 503 when it cannot get a database connection, and it does
// not retry — the failure is handed straight to the caller. On the platform's
// shared Postgres that happens whenever a request needs a NEW connection and
// the server is at its slot ceiling, which is a race one request loses and the
// next one wins. Observed in production: a bulk invite POST 503'd while the
// GETs either side of it succeeded, and PostgREST logged a successful reconnect
// in the same second. Locally there is no contention, so this never appears.
//
// So: retry on 503. The statement never ran (PostgREST fails before it has a
// connection to run it on), which is what makes retrying a POST safe here —
// and every write in this app lands under a unique index anyway, so a
// double-apply would error rather than silently duplicate.
const RETRY_DELAYS_MS = [250, 750]

async function fetchRetrying503(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url

  // Never GoTrue. It issues ONE token for the emailed link and the 6-digit
  // code, so a retried /auth/v1/verify would spend a token the first attempt
  // may already have consumed — the failure mode auth/verify.tsx exists to
  // avoid. Auth calls are also not the ones starving for connections.
  const retriable = !url.includes('/auth/v1/')

  for (let attempt = 0; ; attempt++) {
    const res = await fetch(input, init)
    if (res.status !== 503 || !retriable || attempt >= RETRY_DELAYS_MS.length) return res
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]))
  }
}

export async function initSupabase(): Promise<SupabaseClient> {
  if (client) return client

  const res = await fetch('/config.json', { cache: 'no-store' })
  if (!res.ok) {
    throw new Error(
      `could not load /config.json (${res.status}). ` +
        `In dev, is the tenant running and TENANT_ORIGIN correct in vite.config.ts?`,
    )
  }
  const cfg = (await res.json()) as TenantConfig
  if (!cfg.anonKey) throw new Error('/config.json returned no anonKey')

  client = createClient(cfg.url || window.location.origin, cfg.anonKey, {
    auth: {
      // The magic-link click lands on /verify, which we handle ourselves in
      // auth/verify.tsx — so the SDK must not try to parse the URL itself.
      detectSessionInUrl: false,
      persistSession: true,
      autoRefreshToken: true,
      storageKey: 'teamaccel-auth',
    },
    realtime: { params: { eventsPerSecond: 20 } },
    global: { fetch: fetchRetrying503 },
  })
  return client
}

// Every consumer runs inside <App/>, which only mounts after initSupabase()
// resolves — so this is non-null in practice. Throwing rather than returning
// null keeps every call site free of a nullability dance.
export function sb(): SupabaseClient {
  if (!client) throw new Error('supabase client used before initSupabase() resolved')
  return client
}
